import { useState, useMemo, useId } from 'react';

import { ConfirmacaoDaAcao } from 'components/task-item/modal-actions/moldura-da-acao';
import { useAuth } from 'context/AuthContext';
import { getServerTime } from 'services/dashboard.service';
import { FOCO_VISIVEL } from 'utils/foco';

interface IProps {
  task: any;
  playerInfo: any;
  award?: {
    uau: boolean;
    mandou: boolean;
    valeu: boolean;
  };
  userCodEtapa?: number;
  onSubmit: (task: any) => void;
  onClose: () => void;
}

interface FaixaDeBonificacao {
  /** O valor que o `handleSubmit` traduz em multiplicador, etapa e status. */
  valor: number;
  pontos: string;
  ambito: string;
  legenda: string;
}

const FAIXAS_DA_GERENCIA: FaixaDeBonificacao[] = [
  { valor: 2, pontos: '600 pontos', ambito: 'Gerência de Soluções', legenda: 'Bonificação no nível 1' },
  { valor: 2.1, pontos: '700 pontos', ambito: 'Gerência de Soluções', legenda: 'Promover para o nível 2' },
  { valor: 4, pontos: '1.200 pontos', ambito: 'Gerência Executiva', legenda: 'Bonificação no nível 2' },
  { valor: 4.1, pontos: '1.300 pontos', ambito: 'Gerência Executiva', legenda: 'Promover para o nível 3' },
];

const FAIXA_VALEU: FaixaDeBonificacao = { valor: 8, pontos: '1.500 pontos', ambito: 'Diretoria', legenda: 'Bonificação Valeu' };
const FAIXA_MANDOU: FaixaDeBonificacao = {
  valor: 8.1,
  pontos: '2.500 pontos',
  ambito: 'Diretoria',
  legenda: 'Bonificação Mandou Bem',
};
const FAIXA_UAU: FaixaDeBonificacao = { valor: 8.2, pontos: '5.000 pontos', ambito: 'Diretoria', legenda: 'Bonificação UAU' };

export default function ModalCod5({ task, playerInfo, award, userCodEtapa, onSubmit, onClose }: IProps) {
  const { user } = useAuth();
  const [active, setActive] = useState<number | undefined>();
  const [loading, setLoading] = useState(false);
  const instrucaoId = useId();

  const isVacationManager = useMemo(() => !!playerInfo && !!user && playerInfo._id !== user.extra.id, [playerInfo, user]);

  async function handleSubmit() {
    if (!active || loading) return;

    setLoading(true);

    const updatedData = { ...task };

    if (active === 2) {
      updatedData.multiplicador = 2;
      updatedData.stageNumber = 1;
      updatedData.status = 'concluido';
    } else if (active === 2.1) {
      updatedData.multiplicador = 2;
      updatedData.stageNumber = 2;
      updatedData.status = 'concluido';
    } else if (active === 4) {
      updatedData.multiplicador = 4;
      updatedData.stageNumber = 2;
      updatedData.status = 'concluido';
    } else if (active === 4.1) {
      updatedData.multiplicador = 4;
      updatedData.stageNumber = 3;
      updatedData.status = 'concluido';
    } else if (active === 8) {
      updatedData.multiplicador = 5;
      updatedData.stageNumber = 3;
      updatedData.status = 'valeu';
    } else if (active === 8.1) {
      updatedData.multiplicador = 8;
      updatedData.stageNumber = 3;
      updatedData.status = 'mandou';
    } else if (active === 8.2) {
      updatedData.multiplicador = 16;
      updatedData.stageNumber = 3;
      updatedData.status = 'selo';
      updatedData.evaluator = ['Selo Missão Destaque'];
    }

    if (isVacationManager) {
      if (!updatedData.substitute_managers) {
        updatedData.substitute_managers = [];
      }

      const timestamp = await getServerTime();

      updatedData.substitute_managers.push({
        playerId: user?._id,
        playerName: user?.name,
        status: updatedData.status,
        time: { $date: timestamp },
      });
    }

    onSubmit(updatedData);
  }

  const faixasDaDiretoria =
    userCodEtapa && userCodEtapa === 5 && award
      ? [award.valeu && FAIXA_VALEU, award.mandou && FAIXA_MANDOU, award.uau && FAIXA_UAU].filter(
          (faixa): faixa is FaixaDeBonificacao => !!faixa,
        )
      : [];

  return (
    <ConfirmacaoDaAcao
      title="Bonificar realização"
      subtitle="Você pode bonificar todas as entregas que considerar mais relevantes para o seu âmbito de atuação."
      size="md"
      onClose={onClose}
      onConfirm={() => handleSubmit()}
      confirmLabel="ENVIAR PONTUAÇÃO"
      confirmDisabled={!active || loading}
    >
      <p id={instrucaoId} className="m-0 font-semibold">
        Selecione a pontuação que deseja enviar a essa entrega.
      </p>

      <div role="group" aria-labelledby={instrucaoId}>
        <ul className="m-0 grid list-none grid-cols-2 gap-3 p-0">
          {[...FAIXAS_DA_GERENCIA, ...faixasDaDiretoria].map(faixa => {
            const escolhida = active === faixa.valor;

            return (
              <li key={faixa.valor} className="flex flex-col items-center gap-1.5 text-center">
                <button
                  type="button"
                  aria-pressed={escolhida}
                  onClick={() => setActive(faixa.valor)}
                  disabled={loading}
                  className={`w-full rounded-jj-md border-2 px-3 py-2.5 font-display text-sm font-extrabold uppercase disabled:opacity-50 ${
                    escolhida
                      ? 'border-brand-blue bg-brand-blue text-brand-white'
                      : 'border-surface-200 bg-brand-white text-brand-blue-dark'
                  } ${FOCO_VISIVEL}`}
                >
                  {faixa.pontos}
                  <span className="mt-1 block text-[11px] font-semibold normal-case">{faixa.ambito}</span>
                </button>
                <p className="m-0 text-xs text-brand-blue">{faixa.legenda}</p>
              </li>
            );
          })}
        </ul>
      </div>
    </ConfirmacaoDaAcao>
  );
}
